import React, { useEffect, useRef, useState } from 'react'
import type { Theme } from '@paintscout/ui/styles'
import {
  CircularProgress,
  makeStyles,
  useDebouncedCallback,
  Grid,
  InputField,
  Tabs,
  Tab,
  Typography
} from '@paintscout/ui'
import { useCrmDialogs, useUserPermissions } from '@paintscout/ui-business'
import SearchIcon from '@material-ui/icons/Search'
import type { SearchContactsWithIntegrationQuery } from '@paintscout/api'
import { useSearchContactsWithIntegrationLazyQuery } from '@paintscout/api'
import { jsonToLucene } from 'json-to-lucene'
import { useSnackbar } from 'notistack'
import ContactDialogButton from 'src/components/common/ContactDialogButton'
import type { Filter } from 'json-to-lucene'
import ContactResultsList from '../../dialogs/ContactSearchDialog/ContactResultsList'

// Width by which the `oversized` results dropdown extends past its container, so
// it spans the full dialog (deal dialog or ContactSearchDialog) instead of being
// pinned to the contact column. Mobile uses a smaller value because the surrounding
// dialog is narrower at the `sm` breakpoint and below.
const OVERSIZED_EXPANSION_DESKTOP_PX = 224
const OVERSIZED_EXPANSION_MOBILE_PX = 124

const useStyles = makeStyles<Theme, ContactSearchProps>((theme) => ({
  tabs: {
    paddingLeft: 0,
    paddingBottom: theme.spacing()
  },
  searchAdornment: {
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  results: (props) => ({
    // oversized: offset below the input field to clear DealForm layout; default: overlap slightly above
    top: props.oversized ? 60 : -6,
    overflowY: 'auto',
    position: 'absolute',
    backgroundColor: theme.palette.background.paper,
    zIndex: 21474899999,
    // oversized: expand to fill the surrounding dialog past the contact column
    width: props.oversized ? `calc(100% + ${OVERSIZED_EXPANSION_DESKTOP_PX}px)` : 'calc(100% - 16px)',
    boxShadow: theme.shadows[2],
    [theme.breakpoints.down('sm')]: {
      width: props.oversized ? `calc(100% + ${OVERSIZED_EXPANSION_MOBILE_PX}px)` : 'calc(100% - 16px)'
    }
  }),
  noResults: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2, 0),
    ...theme.typography.overline,
    color: theme.paintscout.palette.brand.Neutral.P80
  },
  addContactButtonWrapper: {
    display: 'flex',
    alignItems: 'flex-end'
  },
  addContactButton: {
    marginTop: 0
  },
  searchContainer: {
    // marginBottom: theme.spacing(1)
  },
  searchFieldWrapper: {
    position: 'relative'
  }
}))

interface ContactSearchProps {
  onSelectContact: (contact: any) => void
  integrations: {
    paintscout?: boolean
    pipelineDeals?: boolean
    serviceTitan?: boolean
    salesforce?: boolean
  }
  query?: string
  disabled?: boolean
  oversized?: boolean
  onCreateNew?: (query: string) => any
  showCreateNew?: boolean
  isFirstSearch?: boolean // DealForm mounts/unmounts this component like crazy so we have to track this externally
  setIsFirstSearch?: (isFirstSearch: boolean) => void
  filters?: Filter[]
  onDialogBack?: (event: React.MouseEvent<HTMLElement>) => void
  onDialogCancel?: (event: React.MouseEvent<HTMLElement>) => void
}

function ContactSearch(props: ContactSearchProps) {
  const classes = useStyles(props)
  const {
    integrations,
    onSelectContact,
    disabled,
    onCreateNew,
    showCreateNew,
    isFirstSearch,
    setIsFirstSearch,
    filters: propFilters,
    onDialogBack,
    onDialogCancel
  } = props
  const { crmContext } = useCrmDialogs()
  const {
    filters: crmFilters = [],
    autoSelectSingleResult = false,
    doWildcardQueryOnMount = false
  } = crmContext?.contact?.search || {}
  const filters = propFilters || crmFilters

  const fetchingContact = useRef<boolean>(false)
  const { enqueueSnackbar } = useSnackbar()
  const [query, setQuery] = useState(props.query || '')
  const [selectedIntegration, setSelectedIntegration] = useState(
    integrations.pipelineDeals
      ? 'pipeline_deals'
      : integrations.serviceTitan
        ? 'service_titan_customers'
        : integrations.salesforce
          ? 'salesforce_contacts'
          : 'paintscout'
  )
  const { permissionToCreateContact } = useUserPermissions()
  const [hideResults, setHideResults] = useState(doWildcardQueryOnMount ? true : false)
  const bookmark = useRef<string>()
  const sort = '-updated_date'

  useEffect(() => {
    setQuery(props.query || '')
  }, [props.query])

  // paintscout only for now, but in case we want more i'm using this one
  const [search, { loading, data, error, fetchMore }] = useSearchContactsWithIntegrationLazyQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    onCompleted: (d: SearchContactsWithIntegrationQuery) => {
      const newBookmark = d?.paintscout?.bookmark

      if (newBookmark && newBookmark !== bookmark.current) {
        bookmark.current = newBookmark
      }

      if (isFirstSearch && setIsFirstSearch) {
        setIsFirstSearch(false)
      }
    }
  })
  // Handle load more for this lazy search on scroll, but debounce it
  const loadMoreHandler = () => {
    // only fetch more results for paintscout results
    if (!loading && selectedIntegration === 'paintscout' && hasMore) {
      fetchMore({
        variables: {
          paintscoutQuery: jsonToLucene({ query, filters }) || '*:*',
          integrationQuery: '',
          useIntegration: false,
          integration: 'paintscout',
          bookmark: bookmark.current,
          sort
        },
        updateQuery: (previousResult: SearchContactsWithIntegrationQuery, { fetchMoreResult }) => {
          if (!fetchMoreResult || !fetchMoreResult.paintscout) {
            return previousResult
          }

          return {
            ...previousResult,
            ...fetchMoreResult,
            paintscout: {
              ...previousResult.paintscout,
              ...fetchMoreResult.paintscout,
              bookmark: fetchMoreResult.paintscout.bookmark,
              contacts: [...previousResult.paintscout.contacts, ...fetchMoreResult.paintscout.contacts]
            }
          }
        }
      })
    }
  }
  // Query and fetchMore debouncers
  const debouncedQuery = useDebouncedCallback(search, 250)
  const handleLoadMore = useDebouncedCallback(loadMoreHandler, 250)
  const hasQuery = !!query.trim()

  useEffect(() => {
    if (isFirstSearch && doWildcardQueryOnMount) {
      debouncedQuery({
        variables: {
          paintscoutQuery: jsonToLucene({ query: '*', filters }),
          integrationQuery: query,
          useIntegration: selectedIntegration !== 'paintscout',
          integration: selectedIntegration,
          sort
        }
      })
    }
  }, [])

  // On text change re-query
  useEffect(() => {
    if ((hasQuery || selectedIntegration === 'service_titan_customers') && debouncedQuery) {
      debouncedQuery({
        variables: {
          paintscoutQuery: jsonToLucene({ query, filters }),
          integrationQuery: query,
          useIntegration: selectedIntegration !== 'paintscout',
          integration: selectedIntegration,
          sort
        }
      })
    }
  }, [debouncedQuery, query, selectedIntegration])

  useEffect(() => {
    if (error) {
      console.error(error)
      enqueueSnackbar('Unable to search Contacts', { variant: 'error' })
    }
  }, [error])

  const paintscoutContacts = data?.paintscout?.contacts ?? []
  const integrationContacts = data?.integration?.results ?? []
  const contacts = selectedIntegration === 'paintscout' ? paintscoutContacts : integrationContacts
  const numResults = selectedIntegration === 'paintscout' ? data?.paintscout?.total_rows ?? 0 : contacts.length
  const hasMore = contacts.length < numResults
  const needsTabs = Object.values(integrations).filter((i) => i).length > 1
  const inputRef = useRef<HTMLDivElement | null>(null)
  const resultsMouseDown = useRef(false)

  if (autoSelectSingleResult && isFirstSearch && contacts.length === 1) {
    if (setIsFirstSearch) {
      setIsFirstSearch(false)
    }
    onSelectContact(contacts[0])
    return null // Prevent rendering the search UI if we auto-select
  }

  return (
    <>
      {needsTabs && (
        <Tabs
          className={classes.tabs}
          value={selectedIntegration}
          onChange={(_, value) => {
            setSelectedIntegration(value)
            bookmark.current = undefined
          }}
        >
          {integrations.paintscout && <Tab label="PaintScout" value="paintscout" disabled={disabled} />}
          {integrations.pipelineDeals && <Tab label="Pipeline CRM" value="pipeline_deals" disabled={disabled} />}
          {integrations.serviceTitan && (
            <Tab label="Service Titan" value="service_titan_customers" disabled={disabled} />
          )}
          {integrations.salesforce && <Tab label="Salesforce" value="salesforce_contacts" disabled={disabled} />}
        </Tabs>
      )}

      <Grid container spacing={2} className={classes.searchContainer}>
        <Grid item xs>
          <div className={classes.searchFieldWrapper}>
            <InputField
              className="tour-contact-search"
              id="query"
              inputRef={inputRef}
              label={'Contact'}
              autoFocus={isFirstSearch && doWildcardQueryOnMount ? false : true}
              fullWidth
              required
              error={!hasQuery}
              disabled={disabled}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setHideResults(false)
              }}
              onFocus={() => {
                setHideResults(false)
              }}
              onBlur={() => {
                // Don't hide results if user is clicking within the results list
                if (resultsMouseDown.current) {
                  resultsMouseDown.current = false
                  return
                }
                setHideResults(true)
              }}
              placeholder="Search existing contacts"
              autoComplete={'off'}
              startAdornment={
                <div className={classes.searchAdornment}>
                  {loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                </div>
              }
              endAdornment={
                numResults > 0 && !hideResults ? (
                  <Typography variant="subtitle1">
                    {numResults} result{numResults > 1 ? 's' : ''}
                  </Typography>
                ) : null
              }
            />
            {contacts.length > 0 && !hideResults && (
              <div
                className={classes.results}
                onMouseDown={() => {
                  resultsMouseDown.current = true
                }}
                onMouseUp={() => {
                  resultsMouseDown.current = false
                }}
                onMouseLeave={() => {
                  resultsMouseDown.current = false
                }}
              >
                <ContactResultsList
                  results={contacts}
                  disabled={disabled}
                  onClickAway={(event) => {
                    const target = event.target as Node
                    if (inputRef.current?.contains(target)) return
                    setHideResults(true)
                  }}
                  onContactClick={onSelectContact}
                  onLoadMore={() => {
                    fetchingContact.current = true
                    handleLoadMore()
                    fetchingContact.current = false
                  }}
                />
              </div>
            )}
          </div>
        </Grid>
        {(onCreateNew || showCreateNew) && (
          <Grid item xs={'auto'} className={classes.addContactButtonWrapper}>
            <ContactDialogButton
              label="New"
              onCreateNew={onCreateNew}
              onConfirm={onSelectContact}
              size="medium"
              variant="dialog"
              inputHeight
              disabled={!permissionToCreateContact}
              className={classes.addContactButton}
              onBack={onDialogBack}
              onCancel={onDialogCancel}
            />
          </Grid>
        )}
      </Grid>
    </>
  )
}

export default ContactSearch
